import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Plus, Loader2, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const fmt = (dateStr) => {
  if (!dateStr) return '-';
  // Parse as local date (not UTC) to avoid timezone-offset day-shift
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtPrice = (val) => {
  if (val == null || val === '') return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);
};

const EMPTY_FORM = {
  entityId: '',
  pct: '',
  dateAcquired: '',
  purchasePrice: '',
  interestSold: false,
  dateSold: '',
  salePrice: '',
  notes: '',
};

export default function SyndicateOwnershipTab({ syndicateId, canEdit = true }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: ownership = [], isLoading: loading } = useQuery({
    queryKey: QK.syndicateOwnership(syndicateId),
    queryFn: () => api.get(`/syndicate-ownership/${syndicateId}`).then(r => r.data),
    enabled: !!syndicateId,
  });

  const { data: crmEntities = [] } = useQuery({
    queryKey: QK.crmEntities({ crm_class: 'account' }),
    queryFn: () => api.get('/crm-entities?crm_class=account').then(r => r.data),
    enabled: !!syndicateId,
  });

  const invalidateOwnership = () => qc.invalidateQueries({ queryKey: QK.syndicateOwnership(syndicateId) });

  // Detail dialog (view mode)
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Add / Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleRowClick = (rec) => { setSelectedRecord(rec); setDetailOpen(true); };

  const handleEditFromDetail = () => {
    setDetailOpen(false);
    openEditDialog(selectedRecord);
  };

  const openEditDialog = (rec = null) => {
    setEditingRecord(rec);
    setForm(rec ? {
      entityId: rec.owner_crm_entity_id || '',
      pct: rec.ownership_percent != null ? String(rec.ownership_percent) : '',
      dateAcquired: rec.date_acquired || '',
      purchasePrice: rec.purchase_price != null ? String(rec.purchase_price) : '',
      interestSold: rec.interest_sold || false,
      dateSold: rec.date_sold || '',
      salePrice: rec.sale_price != null ? String(rec.sale_price) : '',
      notes: rec.notes || '',
    } : EMPTY_FORM);
    setEditOpen(true);
  };

  const closeEditDialog = () => {
    setEditOpen(false);
    setEditingRecord(null);
    setForm(EMPTY_FORM);
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleSave = async () => {
    if (!form.entityId) { toast.error('Please select an owner'); return; }
    const pct = parseFloat(form.pct);
    if (!pct || pct <= 0 || pct > 100) { toast.error('Ownership % must be between 0 and 100'); return; }
    setSaving(true);
    try {
      const payload = {
        ownership_percent: pct,
        date_acquired: form.dateAcquired || '',
        purchase_price: form.purchasePrice ? parseFloat(form.purchasePrice) : null,
        interest_sold: form.interestSold,
        date_sold: form.interestSold ? (form.dateSold || '') : '',
        sale_price: form.interestSold && form.salePrice ? parseFloat(form.salePrice) : null,
        notes: form.notes,
      };
      if (editingRecord) {
        await api.put(`/syndicate-ownership/${editingRecord.id}`, payload);
        toast.success('Ownership updated');
      } else {
        await api.post(`/syndicate-ownership/${syndicateId}`, {
          ...payload,
          owner_crm_entity_id: form.entityId,
        });
        toast.success('Owner added');
      }
      closeEditDialog();
      invalidateOwnership();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save ownership');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingRecord) return;
    setDeleting(true);
    try {
      await api.delete(`/syndicate-ownership/${editingRecord.id}`);
      toast.success('Owner removed');
      closeEditDialog();
      invalidateOwnership();
    } catch {
      toast.error('Failed to remove owner');
    } finally {
      setDeleting(false);
    }
  };

  const currentTotal = ownership.reduce((sum, r) => sum + (r.ownership_percent || 0), 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Syndicate Ownership</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Total: {currentTotal.toFixed(1)}%{' '}
                {currentTotal === 100
                  ? '✓'
                  : currentTotal > 100
                    ? `(over-allocated by ${(currentTotal - 100).toFixed(1)}%)`
                    : `(${(100 - currentTotal).toFixed(1)}% remaining)`}
              </p>
            </div>
            {canEdit && (
              <Button onClick={() => openEditDialog()} variant="ghost" size="sm" data-testid="add-syndicate-owner-btn">
                <Plus className="w-4 h-4 mr-2" /> Add
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {ownership.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No owners added yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-2" style={{ minWidth: '500px' }}>
                <div className="grid grid-cols-4 gap-4 pb-2 border-b font-semibold text-xs text-muted-foreground uppercase">
                  <div>Owner</div>
                  <div>Ownership %</div>
                  <div>Date Acquired</div>
                  <div>Notes</div>
                </div>
                {ownership.map(rec => (
                  <div
                    key={rec.id}
                    className="grid grid-cols-4 gap-4 p-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => handleRowClick(rec)}
                    data-testid={`ownership-row-${rec.id}`}
                  >
                    <div>
                      <p
                        className="text-sm font-medium text-primary hover:underline"
                        onClick={e => { e.stopPropagation(); navigate(`/crm/accounts/${rec.owner_crm_entity_id}`); }}
                      >
                        {rec.owner_name || rec.owner_crm_entity_id}
                      </p>
                      {rec.interest_sold && (
                        <span className="text-[10px] text-amber-600 font-medium">Interest sold</span>
                      )}
                    </div>
                    <div><p className="text-sm">{rec.ownership_percent?.toFixed(1)}%</p></div>
                    <div><p className="text-sm text-muted-foreground">{rec.date_acquired ? fmt(rec.date_acquired) : '-'}</p></div>
                    <div><p className="text-sm text-muted-foreground truncate">{rec.notes || '-'}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Detail Dialog ──────────────────────────────────────────── */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ownership Record Details</DialogTitle>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Owner</p>
                  <p
                    className="font-medium text-primary hover:underline cursor-pointer"
                    onClick={() => { setDetailOpen(false); navigate(`/crm/accounts/${selectedRecord.owner_crm_entity_id}`); }}
                  >
                    {selectedRecord.owner_name || selectedRecord.owner_crm_entity_id}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Ownership %</p>
                  <p className="font-medium">{selectedRecord.ownership_percent?.toFixed(1)}%</p>
                </div>
                {selectedRecord.date_acquired && (
                  <div>
                    <p className="text-xs text-muted-foreground">Date Acquired</p>
                    <p className="font-medium">{fmt(selectedRecord.date_acquired)}</p>
                  </div>
                )}
                {selectedRecord.purchase_price != null && (
                  <div>
                    <p className="text-xs text-muted-foreground">Purchase Price</p>
                    <p className="font-medium">{fmtPrice(selectedRecord.purchase_price)}</p>
                  </div>
                )}
                {selectedRecord.interest_sold && (
                  <>
                    <div>
                      <p className="text-xs text-muted-foreground">Interest Sold</p>
                      <p className="font-medium text-amber-600">Yes</p>
                    </div>
                    {selectedRecord.date_sold && (
                      <div>
                        <p className="text-xs text-muted-foreground">Date Sold</p>
                        <p className="font-medium">{fmt(selectedRecord.date_sold)}</p>
                      </div>
                    )}
                    {selectedRecord.sale_price != null && (
                      <div>
                        <p className="text-xs text-muted-foreground">Sale Price</p>
                        <p className="font-medium">{fmtPrice(selectedRecord.sale_price)}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
              {selectedRecord.notes && (
                <div>
                  <p className="text-xs text-muted-foreground">Notes</p>
                  <p className="text-sm mt-1">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <div className="flex justify-between w-full">
              {canEdit ? (
                <Button variant="ghost" size="icon" onClick={handleEditFromDetail} title="Edit record" data-testid="edit-ownership-pencil">
                  <Pencil className="w-4 h-4" />
                </Button>
              ) : <span />}
              <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add / Edit Dialog ──────────────────────────────────────── */}
      <Dialog open={editOpen} onOpenChange={open => { if (!open) closeEditDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Edit Ownership' : 'Add Syndicate Owner'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Owner */}
            <div className="space-y-2">
              <Label>Owner Account <span className="text-red-500">*</span></Label>
              {editingRecord ? (
                <p className="text-sm font-medium py-2 px-3 bg-muted/40 rounded border">
                  {editingRecord.owner_name || editingRecord.owner_crm_entity_id}
                </p>
              ) : (
                <Select value={form.entityId || '_none'} onValueChange={v => setForm(f => ({ ...f, entityId: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select owner…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Select owner —</SelectItem>
                    {crmEntities.filter(e => !e.is_deleted && !e.is_tenant_managed).map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.system_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Ownership % */}
            <div className="space-y-2">
              <Label>Ownership % <span className="text-red-500">*</span></Label>
              <Input
                type="number" min="0.1" max="100" step="0.1"
                value={form.pct} onChange={set('pct')}
                placeholder="e.g. 25"
                data-testid="ownership-pct-input"
              />
              {!editingRecord && (
                <p className="text-xs text-muted-foreground">Remaining: {Math.max(0, 100 - currentTotal).toFixed(1)}%</p>
              )}
            </div>

            {/* Date Acquired + Purchase Price */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date Acquired</Label>
                <Input type="date" value={form.dateAcquired} onChange={set('dateAcquired')} />
              </div>
              <div className="space-y-2">
                <Label>Purchase Price</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={form.purchasePrice} onChange={set('purchasePrice')}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Interest Sold checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="interest-sold"
                checked={form.interestSold}
                onCheckedChange={v => setForm(f => ({ ...f, interestSold: v }))}
                data-testid="interest-sold-checkbox"
              />
              <Label htmlFor="interest-sold" className="text-sm font-normal cursor-pointer">
                Interest was sold by this owner
              </Label>
            </div>

            {/* Date Sold + Sale Price — only visible when sold */}
            {form.interestSold && (
              <div className="grid grid-cols-2 gap-4 pl-6 border-l-2 border-muted">
                <div className="space-y-2">
                  <Label>Date Sold</Label>
                  <Input type="date" value={form.dateSold} onChange={set('dateSold')} />
                </div>
                <div className="space-y-2">
                  <Label>Sale Price</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={form.salePrice} onChange={set('salePrice')}
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={set('notes')} placeholder="Optional notes" />
            </div>
          </div>

          <DialogFooter>
            <div className="flex justify-between w-full">
              {/* Delete only visible when editing */}
              {editingRecord ? (
                <Button
                  variant="ghost" size="icon"
                  className="text-red-400 hover:text-red-600 hover:bg-red-50"
                  onClick={handleDelete} disabled={deleting}
                  title="Delete ownership record"
                  data-testid="delete-ownership-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="outline" onClick={closeEditDialog}>Cancel</Button>
                <Button
                  className="bg-primary hover:bg-primary/90 text-white"
                  onClick={handleSave}
                  disabled={saving || !form.entityId || form.entityId === '_none' || !form.pct}
                  data-testid="save-ownership-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {editingRecord ? 'Save Changes' : 'Add Owner'}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
